import { FC } from 'react';

import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import groupBy from 'lodash/groupBy';
import { useTranslation } from 'react-i18next';

import { Modal, ModalBody } from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { COUNTRIES, getCountryLabel } from '@/utils/document-utils/countries';
import { sortDocumentsByTags } from '@/utils/formatDocumentTag';

import {
  ChooseDocumentCard,
  ChooseDocumentCountryTabs,
} from '@/components/document';

import { ContentReference } from '@/_generated/api-clients/xp';
import { documentTransientAtom } from '@/atoms/document-transient';
import { userPrefsAtom } from '@/atoms/userPrefs';

interface ChooseDocumentProps {
  icon?: string;
  isOpen: boolean;
  onClick: (document: ContentReference) => void;
  onClose?: () => void;
  references: ContentReference[];
}

const ChooseDocumentModal: FC<ChooseDocumentProps> = ({
  icon = '',
  isOpen,
  onClick,
  onClose,
  references,
}) => {
  const { chooseDocumentVersion } = useFlags();
  const { t } = useTranslation();
  const userPrefs = useAtomValue(userPrefsAtom);
  const { chosenCountryTab } = useAtomValue(documentTransientAtom);
  const { data: auth } = useAuth();
  const isAdminOperatorOrStakeholder = auth?.isAdminOperatorOrStakeholder;

  const enableChooseDocumentVersion =
    chooseDocumentVersion && isAdminOperatorOrStakeholder;

  const referencesGroup = groupBy(references, 'country');
  const userCountryId = userPrefs.country.id;
  const activeCountryTab =
    chosenCountryTab ??
    (userCountryId && referencesGroup[userCountryId]
      ? userCountryId
      : COUNTRIES.find(({ id }) => referencesGroup[id]?.length)?.id);

  const countryHasResource = COUNTRIES.filter(
    ({ id }) => referencesGroup[id]?.length,
  ).map(({ id }) => id);

  const activeReferences = sortDocumentsByTags(
    activeCountryTab ? referencesGroup[activeCountryTab] : undefined,
  );

  return (
    <Modal
      onClose={() => {
        onClose?.();
      }}
      scrollMode="modal-body"
      show={isOpen}
      size="lg"
    >
      <ModalBody>
        <div className="text-grayscale-gray7 pt-12 pb-4 text-[32px] font-bold">
          {t('Generic.chooseVersion')}
        </div>
        <ChooseDocumentCountryTabs
          activeCountryTab={activeCountryTab}
          countryHasResource={countryHasResource}
        />
        <div>
          {!!activeReferences?.length &&
            [...activeReferences].map((document, index) => (
              <ChooseDocumentCard
                icon={icon}
                key={`${document.country}_${index}`}
                name={document.name ?? ''}
                onClick={() => onClick(document)}
                tags={document.tags}
                country={
                  enableChooseDocumentVersion
                    ? getCountryLabel(document.country as string)
                    : ''
                }
              />
            ))}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ChooseDocumentModal;
